import React, { useState } from "react";
import { BookOpen, ZoomIn, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Display the authoritative Hijama Atlas page (by Sheikh Ahmed Al-Hefni)
 *  for a given illness, replacing the synthetic SVG body diagram.
 *
 *  Clicking the image opens a full-screen lightbox for clinical readability.
 */
const AtlasPageView = ({ pageNumber, illnessName, atlasSource }) => {
  const [open, setOpen] = useState(false);
  const src = `${BACKEND}/api/atlas/page/${pageNumber}`;

  return (
    <div data-testid="atlas-page-view" className="rounded-3xl overflow-hidden bg-white border border-gold/30 shadow-sm">
      <div className="px-5 sm:px-7 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-gold/20 bg-cream/40">
        <div className="flex items-center gap-3">
          <BookOpen className="w-5 h-5 text-gold" />
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-clay font-semibold">Hijama Atlas Point Map</p>
            <p className="text-sm text-forest font-medium">{illnessName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-mute">
          <span className="italic">{atlasSource}</span>
          <span className="px-2 py-1 rounded-full bg-forest/10 text-forest font-semibold">Page {pageNumber}</span>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="atlas-page-zoom-trigger"
        className="block w-full group relative"
        title="Click to zoom"
      >
        <img
          src={src}
          alt={`Hijama Atlas — page ${pageNumber} — ${illnessName}`}
          className="block w-full h-auto select-none"
          loading="lazy"
        />
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-forest text-sand px-3 py-1.5 text-[11px] opacity-0 group-hover:opacity-100 transition">
          <ZoomIn className="w-3.5 h-3.5" /> Zoom
        </span>
      </button>

      <p className="px-5 sm:px-7 py-3 text-[11px] text-mute border-t border-gold/20 leading-relaxed">
        Authoritative reference image from Sheikh Ahmed Al-Hefni's Hijama Atlas.
        Always seek a certified Hijama practitioner — do not self-administer wet cupping.
      </p>

      {open && (
        <div
          data-testid="atlas-page-lightbox"
          className="fixed inset-0 z-[200] bg-ink/90 backdrop-blur flex items-center justify-center p-4 overflow-auto"
          onClick={() => setOpen(false)}
        >
          <button
            type="button"
            data-testid="atlas-page-lightbox-close"
            onClick={() => setOpen(false)}
            className="absolute top-5 right-5 rounded-full bg-sand text-forest p-2 shadow-lg hover:bg-cream transition"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={src}
            alt={`Hijama Atlas page ${pageNumber} — full size`}
            className="max-h-[95vh] w-auto rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default AtlasPageView;
